import { useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError, type GroupMemberRow, type MemberGroup } from '../lib/api'
import './group-messages.css'

/** THE GROUPS BUTTON ON MESSAGING (066): every group, and who is in one.
 *
 *  "Your own groups" (kind 'list') are made here -- the board, the event
 *  volunteers -- and anyone can be in as many as apply. Categories and areas
 *  are the groups the roster already has; they are listed here too, because
 *  a group message can go to them, and their members can be changed here a
 *  group at a time as well as on each member's record. A member is in ONE
 *  category: the server refuses a second in words.
 *
 *  GATED ON `settings.members`, like the roster: whoever says who is in an
 *  area says who is on the board. Members only (the customer's Q1 A). */

const KIND_WORD: Record<MemberGroup['kind'], string> = {
  category: 'Category', area: 'Area', list: 'Your own group',
}
type Pick = { id: string; label: string; on_duty: boolean }

export function MessageGroups() {
  const [groups, setGroups] = useState<MemberGroup[]>([])
  const [openId, setOpenId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [naming, setNaming] = useState(false)
  const [name, setName] = useState('')

  const load = useCallback(async () => {
    try {
      const all = await api.memberGroups()
      // Your own groups first: they are what this screen is for.
      const order = { list: 0, category: 1, area: 2 } as const
      setGroups([...all].sort((a, b) => order[a.kind] - order[b.kind]
        || (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name)))
      setError(null)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load the groups.')
    }
  }, [])
  useEffect(() => { load() }, [load])

  async function create() {
    const typed = name.trim()
    if (!typed) return
    try {
      const g = await api.memberGroupCreate({ name: typed, kind: 'list' })
      setNaming(false); setName('')
      await load()
      setOpenId(g.id)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not make the group.')
    }
  }

  const open = groups.find((g) => g.id === openId) ?? null

  return (
    <div className={`gm-page ${open ? 'gm-reading' : ''}`}>
      <div className="gm-col gm-list">
        <div className="gm-head">
          <h2>Groups</h2>
          <span className="gm-spacer" />
          <button type="button" className="btn gm-go" onClick={() => { setNaming(true); setName('') }}>
            + New group
          </button>
        </div>
        {naming && (
          <form className="gm-newgroup" onSubmit={(e) => { e.preventDefault(); create() }}>
            <input className="gm-input" autoFocus value={name} maxLength={80}
                   placeholder="Name it — Board, Event volunteers…"
                   onChange={(e) => setName(e.target.value)} />
            <button type="submit" className="btn gm-go" disabled={!name.trim()}>Make it</button>
            <button type="button" className="btn" onClick={() => setNaming(false)}>Cancel</button>
          </form>
        )}
        <div className="gm-cards">
          {error && <div className="gm-note gm-warn">{error}</div>}
          {/* TWO KINDS, DRAWN APART (the customer, 6 Oct 2026: "I should see a
              difference between those two groups"). The roster's groups --
              what the Dispatch screen aims calls at -- in blue, filled on each
              member's record; his own groups in purple, filled here. */}
          {(['roster', 'own'] as const).map((side) => {
            const these = groups.filter((g) => (g.kind === 'list') === (side === 'own'))
            return (
              <div key={side}>
                <div className="gm-sect">
                  {side === 'own'
                    ? <><span className="gm-tag gm-owntag">Your own groups</span> made here, for messages</>
                    : <><span className="gm-tag gm-rostertag">From the roster</span> how members are already grouped</>}
                </div>
                {these.length === 0 && (
                  <div className="gm-hint gm-pad">
                    {side === 'own' ? 'None yet — + New group makes one.' : 'No categories or areas yet.'}
                  </div>
                )}
                {these.map((g) => (
                  <button type="button" key={g.id}
                          className={`gm-card ${side === 'own' ? 'gm-owncard' : 'gm-rostercard'} ${g.id === openId ? 'gm-on' : ''}`}
                          onClick={() => setOpenId(g.id)}>
                    <span className="gm-t"><b>{g.name}</b><small>{KIND_WORD[g.kind]}</small></span>
                    <span className="gm-s">{g.members ?? 0} {g.members === 1 ? 'member' : 'members'}</span>
                  </button>
                ))}
              </div>
            )
          })}
        </div>
      </div>

      <div className="gm-col gm-main">
        {open
          ? <GroupEditor key={open.id} group={open} onChanged={load} onBack={() => setOpenId(null)} />
          : <div className="gm-empty">Choose a group to see who is in it, or make a new one.</div>}
      </div>
    </div>
  )
}

function GroupEditor({ group, onChanged, onBack }: {
  group: MemberGroup
  onChanged: () => void
  onBack: () => void
}) {
  const [rows, setRows] = useState<GroupMemberRow[] | null>(null)
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null)
  const [term, setTerm] = useState('')
  const [found, setFound] = useState<Pick[]>([])
  const [renaming, setRenaming] = useState(false)
  const [newName, setNewName] = useState(group.name)
  const ask = useRef(0)

  const load = useCallback(async () => {
    try {
      setRows(await api.groupMembers(group.id))
    } catch (e) {
      setNote({ ok: false, text: e instanceof ApiError ? e.message : 'Could not load who is in it.' })
    }
  }, [group.id])
  useEffect(() => { load() }, [load])

  // The roster's own search (item 18): a name, a unit or the digits of a number.
  useEffect(() => {
    const mine = ++ask.current
    if (term.trim().length < 1) { setFound([]); return }
    const t = setTimeout(() => {
      api.memberPick(term.trim())
        .then((r) => { if (ask.current === mine) setFound(r.map((x) => ({ id: x.id, label: x.label, on_duty: x.on_duty }))) })
        .catch(() => { if (ask.current === mine) setFound([]) })
    }, 200)
    return () => clearTimeout(t)
  }, [term])

  async function add(p: Pick) {
    setNote(null)
    try {
      await api.groupMemberAdd(group.id, p.id)
      setTerm(''); setFound([])
      await load(); onChanged()
      setNote({ ok: true, text: `${p.label} is in ${group.name}.` })
    } catch (e) {
      setNote({ ok: false, text: e instanceof ApiError ? e.message : 'Could not add them.' })
    }
  }
  async function remove(r: GroupMemberRow) {
    setNote(null)
    try {
      await api.groupMemberRemove(group.id, r.id)
      await load(); onChanged()
    } catch (e) {
      setNote({ ok: false, text: e instanceof ApiError ? e.message : 'Could not take them out.' })
    }
  }
  async function rename() {
    try {
      await api.memberGroupUpdate(group.id, { name: newName.trim() })
      setRenaming(false); onChanged()
    } catch (e) {
      setNote({ ok: false, text: e instanceof ApiError ? e.message : 'Could not rename it.' })
    }
  }
  async function retire() {
    try {
      await api.memberGroupUpdate(group.id, { active: false })
      onChanged(); onBack()
    } catch (e) {
      setNote({ ok: false, text: e instanceof ApiError ? e.message : 'Could not retire it.' })
    }
  }

  const inIt = new Set((rows ?? []).map((r) => r.id))
  /** Only "your own groups" are filled here; the roster's are set on each
   *  member's record, so Dispatch and Messaging can never disagree. */
  const own = group.kind === 'list'
  return (
    <div className="gm-detail">
      <div className="gm-head">
        <button type="button" className="btn sm gm-back" onClick={onBack}>← Groups</button>
        {renaming ? (
          <form className="gm-newgroup gm-inline" onSubmit={(e) => { e.preventDefault(); rename() }}>
            <input className="gm-input" autoFocus value={newName} maxLength={80}
                   onChange={(e) => setNewName(e.target.value)} />
            <button type="submit" className="btn gm-go" disabled={!newName.trim()}>Save</button>
            <button type="button" className="btn" onClick={() => setRenaming(false)}>Cancel</button>
          </form>
        ) : <h2>{group.name} <span className={`gm-tag ${own ? 'gm-owntag' : 'gm-rostertag'}`}>{own ? 'Your own group' : `From the roster · ${KIND_WORD[group.kind]}`}</span></h2>}
        <span className="gm-spacer" />
        {own && !renaming && <button type="button" className="btn sm" onClick={() => setRenaming(true)}>Rename</button>}
        {own && !renaming && <button type="button" className="btn sm" onClick={retire}
                              title="Retired, never deleted: messages already sent to it keep its name.">Retire</button>}
      </div>
      <div className="gm-body">
        {!own && (
          <div className="gm-note gm-lock">
            This group comes from the roster — the same one the Dispatch screen sends calls to.
            Who is in it is set on each member's record (Settings, Members), so the two can never
            disagree.
          </div>
        )}
        {own && <>
        <label className="gm-lab" htmlFor="gm-addm">Add a member</label>
        <div className="gm-search">
          <input id="gm-addm" className="gm-input" value={term} autoComplete="off"
                 placeholder="Type a name, unit number or phone number…"
                 onChange={(e) => setTerm(e.target.value)} />
          {found.length > 0 && (
            <div className="gm-found" role="listbox">
              {found.slice(0, 8).map((p) => (
                <button type="button" key={p.id} role="option" aria-selected={false}
                        disabled={inIt.has(p.id)} onClick={() => add(p)}>
                  {p.label}
                  {inIt.has(p.id) ? <small>already in it</small> : p.on_duty ? <small>on duty</small> : null}
                </button>
              ))}
            </div>
          )}
        </div>
        </>}
        {note && <div className={`gm-note ${note.ok ? 'gm-good' : 'gm-warn'}`}>{note.text}</div>}
        {rows === null ? <div className="gm-empty">Loading…</div> : rows.length === 0 ? (
          <div className="gm-empty">Nobody in {group.name} yet.</div>
        ) : (
          <table className="gm-table">
            <thead><tr><th>Member</th><th>Reached by</th><th /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.callsign && r.label && r.label !== r.callsign ? `${r.callsign} · ${r.label}` : (r.label ?? r.callsign ?? 'a member')}</td>
                  <td>
                    {[r.by_text && 'Text', r.by_email && 'Email'].filter(Boolean).join(', ')
                      || <span className="gm-warn">No text or email — cannot be reached</span>}
                  </td>
                  <td className="gm-right">
                    {own && <button type="button" className="btn sm" onClick={() => remove(r)}>Remove</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
